import { useRef, useState } from "react";
import { dropdownCategories, dropdownValues } from "../config/options.js";
import { ownerLabel } from "../lib/format.js";
import { AdminOrganization } from "../components/adminOrganization.jsx";
import {
  AdminLaunchSection,
  TrialWorkspaceAccessNotice,
  canCreateCustomerWorkspaces,
  shouldShowTrialWorkspaceAccessNotice,
} from "../components/adminTrialWorkspaces.jsx";
import { CustomerLink, EmployeeLink } from "../components/recordLinks.jsx";
import { TeamTreeTable } from "../components/teamTreeTable.jsx";
import { AsyncActionButton, CheckboxField, Metric, Panel, ResourceTable, SelectField, SimpleForm, TextField, Topbar } from "../components/ui.jsx";

export function AdminView({ resources, users, customers = [], report, user, focusEmployeeId, onEmployeeFocusHandled, openCustomer, openEmployee, onCreate, onUpdate }) {
  const [fieldForm, setFieldForm] = useState({ label: "", key: "", appliesTo: "all", fieldType: "text", customerTypes: "", industryTypes: "", options: "", required: false });
  const [dropdownForm, setDropdownForm] = useState({ id: "", category: "leadSource", label: "", value: "", sortOrder: 10, active: true });
  const dropdownEditorRef = useRef(null);
  const overview = resources.adminOverview;
  const fullUserById = new Map(users.map((member) => [member.id, member]));
  const adminUsers = overview?.users?.length
    ? overview.users.map((member) => ({ ...(fullUserById.get(member.id) || {}), ...member }))
    : [];
  const teams = overview?.teams ?? [];
  const workspaces = overview?.workspaces ?? (overview?.workspace ? [overview.workspace] : []);
  const canCreateWorkspaces = canCreateCustomerWorkspaces(user, overview);
  const showTrialAccessNotice = shouldShowTrialWorkspaceAccessNotice(user, overview);
  const activityCustomFields = resources.activityCustomFields ?? [];
  const dropdownOptions = resources.dropdownOptions ?? [];
  const customerTypeScopeOptions = dropdownValues(dropdownOptions, "customerType");
  const industryTypeScopeOptions = dropdownValues(dropdownOptions, "industryType");
  const customerDeletionRequests = resources.customerDeletionRequests ?? [];
  const customerById = new Map(customers.map((customer) => [customer.id, customer]));
  const adminUserById = new Map(adminUsers.map((member) => [member.id, member]));
  const teamName = (teamId) => teams.find((team) => team.id === teamId)?.name || teamId || "Root";
  const teamRollupRows = report?.teamRollups?.length
    ? report.teamRollups
    : teams.map((team) => ({
        id: team.id,
        teamId: team.id,
        teamName: team.name,
        parentTeamId: team.parentTeamId || "",
        parentTeamName: teamName(team.parentTeamId),
        managerId: team.managerId || "",
        managerName: ownerLabel(adminUsers, team.managerId),
        childTeams: teams.filter((item) => item.parentTeamId === team.id).length,
        salesReps: adminUsers.filter((user) => user.teamId === team.id && user.role === "sales" && user.active !== false).length,
        leads: 0,
        activePipeline: 0,
        followUpsDue: 0,
        calls: 0,
        visits: 0,
        callVisitRatio: 0,
        orders: 0,
        converted: 0,
        lost: 0,
        conversionRate: 0,
        achievedRevenue: 0,
        revenueTarget: 0,
        pendingRevenueGap: 0,
        averageOrderValue: 0,
        revenuePerRep: 0,
        achievementPercent: 0,
        status: "Watch",
      }));

  function confirmDeletionReview(row, status) {
    const action = status === "Approved" ? "Approve permanent removal" : "Deny removal";
    const consequence =
      status === "Approved"
        ? "This removes the prospect from active views, keeps the database history, and cancels pending planned visits."
        : "The prospect will remain available in active views.";
    const confirmed = window.confirm(`${action} for ${row.customerName}? ${consequence}`);
    if (!confirmed) return true;
    return onUpdate(`/customer-deletion-requests/${row.id}`, { status });
  }

  async function saveDropdownOption() {
    const payload = {
      category: dropdownForm.category,
      label: dropdownForm.label,
      value: dropdownForm.value || dropdownForm.label,
      sortOrder: dropdownForm.sortOrder,
      active: dropdownForm.active,
    };
    const saved = dropdownForm.id
      ? await onUpdate(`/dropdown-options/${dropdownForm.id}`, payload)
      : await onCreate("/dropdown-options", payload);
    if (saved === false) throw new Error("Save failed");
    resetDropdownForm(dropdownForm.category);
  }

  function resetDropdownForm(category = dropdownForm.category) {
    const categoryCount = dropdownOptions.filter((option) => option.category === category).length;
    setDropdownForm({ id: "", category, label: "", value: "", sortOrder: (categoryCount + 1) * 10, active: true });
  }

  function editDropdownOption(row) {
    setDropdownForm({
      id: row.id,
      category: row.category,
      label: row.label,
      value: row.value,
      sortOrder: row.sortOrder ?? 10,
      active: row.active !== false,
    });
    window.setTimeout(() => {
      dropdownEditorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      dropdownEditorRef.current?.querySelector("input")?.focus({ preventScroll: true });
    }, 0);
  }

  function setDropdownVisibility(row, active) {
    const verb = active ? "Restore" : "Delete";
    const confirmed = window.confirm(`${verb} ${row.label} from ${formatDropdownCategory(row.category)}? Existing records keep their saved value.`);
    if (!confirmed) return true;
    return onUpdate(`/dropdown-options/${row.id}`, { active });
  }

  function toggleFieldScope(field, value) {
    const currentValues = parseCsvScope(fieldForm[field]);
    const valueKey = String(value || "").trim().toLowerCase();
    const hasValue = currentValues.some((item) => item.toLowerCase() === valueKey);
    const nextValues = hasValue
      ? currentValues.filter((item) => item.toLowerCase() !== valueKey)
      : [...currentValues, value];
    setFieldForm({ ...fieldForm, [field]: nextValues.join(", ") });
  }

  return (
    <>
      <Topbar title="Admin & Audit" subtitle="User, role, team, territory, and audit trail controls for supervisor visibility." />
      <section className="metric-grid">
        <Metric label="Users" value={overview?.users?.length ?? users.length} />
        <Metric label="Teams" value={overview?.teams?.length ?? 0} />
        {canCreateWorkspaces ? <Metric label="Workspaces" value={workspaces.length} /> : null}
        <Metric label="Products" value={overview?.counts?.products ?? 0} />
        <Metric label="Audit Events" value={resources.auditLogs.length} />
      </section>
      {canCreateWorkspaces ? <AdminLaunchSection workspaces={workspaces} onCreate={onCreate} /> : null}
      {showTrialAccessNotice ? <TrialWorkspaceAccessNotice /> : null}
      {overview ? (
        <AdminOrganization
          teams={teams}
          users={adminUsers}
          currentUser={user}
          dropdownOptions={dropdownOptions}
          passwordRequired={overview?.workspace?.loginMode === "password"}
          focusEmployeeId={focusEmployeeId}
          onEmployeeFocusHandled={onEmployeeFocusHandled}
          openEmployee={openEmployee}
          onCreate={onCreate}
          onUpdate={onUpdate}
        />
      ) : (
        <Panel title="Organization Management" subtitle="Loading secure employee and team controls." />
      )}
      <section className="admin-setup-grid">
        <div className="admin-card-stack">
          <Panel title="Activity Field Request" subtitle="Create optional or required fields for calls and visits. Leave customer/domain scope blank to apply to every business.">
            <SimpleForm
              onSubmit={async () => {
                const saved = await onCreate("/activity-custom-fields", {
                  ...fieldForm,
                  customerTypes: parseCsvScope(fieldForm.customerTypes),
                  industryTypes: parseCsvScope(fieldForm.industryTypes),
                  options: fieldForm.options.split(",").map((option) => option.trim()).filter(Boolean),
                });
                if (saved === false) throw new Error("Save failed");
                setFieldForm({ label: "", key: "", appliesTo: fieldForm.appliesTo, fieldType: fieldForm.fieldType, customerTypes: "", industryTypes: "", options: "", required: false });
              }}
            >
              <TextField label="Field label" value={fieldForm.label} required maxLength={80} onChange={(label) => setFieldForm({ ...fieldForm, label })} />
              <TextField label="Field key" value={fieldForm.key} maxLength={60} onChange={(key) => setFieldForm({ ...fieldForm, key })} />
              <SelectField
                label="Applies to"
                value={fieldForm.appliesTo}
                options={[
                  { value: "all", label: "Calls & visits" },
                  { value: "call", label: "Calls only" },
                  { value: "visit", label: "Visits only" },
                ]}
                onChange={(appliesTo) => setFieldForm({ ...fieldForm, appliesTo })}
              />
              <SelectField
                label="Field type"
                value={fieldForm.fieldType}
                options={[
                  { value: "text", label: "Text" },
                  { value: "number", label: "Number" },
                  { value: "date", label: "Date" },
                  { value: "select", label: "Select list" },
                ]}
                onChange={(fieldType) => setFieldForm({ ...fieldForm, fieldType })}
              />
              <TextField
                label="Customer types"
                value={fieldForm.customerTypes}
                maxLength={220}
                onChange={(nextCustomerTypes) => setFieldForm({ ...fieldForm, customerTypes: nextCustomerTypes })}
              />
              <ScopeQuickPick
                label="Customer type scope"
                values={customerTypeScopeOptions}
                selectedValues={parseCsvScope(fieldForm.customerTypes)}
                onToggle={(value) => toggleFieldScope("customerTypes", value)}
              />
              <TextField
                label="Business domains"
                value={fieldForm.industryTypes}
                maxLength={400}
                onChange={(nextIndustryTypes) => setFieldForm({ ...fieldForm, industryTypes: nextIndustryTypes })}
              />
              <ScopeQuickPick
                label="Business domain scope"
                values={industryTypeScopeOptions}
                selectedValues={parseCsvScope(fieldForm.industryTypes)}
                onToggle={(value) => toggleFieldScope("industryTypes", value)}
              />
              <TextField label="Select options" value={fieldForm.options} maxLength={400} onChange={(options) => setFieldForm({ ...fieldForm, options })} />
              <CheckboxField label="Required when logging" checked={fieldForm.required} onChange={(required) => setFieldForm({ ...fieldForm, required })} />
            </SimpleForm>
          </Panel>
          <div className="editor-anchor" ref={dropdownEditorRef}>
            <Panel
              title={dropdownForm.id ? "Edit Dropdown Option" : "Dropdown Options"}
              subtitle={
                dropdownForm.id
                  ? "The selected dropdown value is loaded here. Update the fields and save to apply changes."
                  : "Add, edit, hide, or restore the dropdown choices used by prospects and activity logging."
              }
            >
              <SimpleForm onSubmit={saveDropdownOption} submitLabel={dropdownForm.id ? "Update Dropdown" : "Save Dropdown"} submittingLabel={dropdownForm.id ? "Updating..." : "Saving..."}>
                <SelectField
                  label="Dropdown"
                  value={dropdownForm.category}
                  options={dropdownCategories.map((category) => ({ value: category.key, label: `${category.area} - ${category.label}` }))}
                  onChange={(category) => resetDropdownForm(category)}
                />
                <TextField label="Option label" value={dropdownForm.label} required maxLength={80} onChange={(label) => setDropdownForm({ ...dropdownForm, label })} />
                <TextField label="Saved value" value={dropdownForm.value} maxLength={80} onChange={(value) => setDropdownForm({ ...dropdownForm, value })} />
                <TextField label="Display order" type="number" min="0" max="10000" value={dropdownForm.sortOrder} onChange={(sortOrder) => setDropdownForm({ ...dropdownForm, sortOrder })} />
                <CheckboxField label="Visible in dropdown" checked={dropdownForm.active} onChange={(active) => setDropdownForm({ ...dropdownForm, active })} />
                {dropdownForm.id ? (
                  <button className="ghost-btn full" type="button" onClick={() => resetDropdownForm(dropdownForm.category)}>
                    Cancel Editing Dropdown
                  </button>
                ) : null}
              </SimpleForm>
            </Panel>
          </div>
        </div>
      </section>
      <section className="panel spaced">
        <h2>Team Hierarchy & Metrics</h2>
        <TeamTreeTable rows={teamRollupRows} onOpenEmployee={openEmployee} />
      </section>
      <section className="admin-wide-stack">
        <Panel title="Activity Field Approvals" subtitle="Only approved fields are visible to managers and employees while logging activities.">
          <ResourceTable
            rows={activityCustomFields}
            columns={[
              ["label", "Label"],
              ["key", "Key"],
              ["appliesTo", "Applies To", formatActivityFieldScope],
              ["customerTypes", "Customer Types", formatScopeList],
              ["industryTypes", "Domains", formatScopeList],
              ["fieldType", "Type", formatActivityFieldType],
              ["required", "Required", (value) => (value ? "Yes" : "No")],
              ["status", "Status"],
              ["options", "Options", (value) => (value?.length ? value.join(", ") : "-")],
            ]}
            action={(row) => (
              <div className="row-actions">
                {row.status !== "Approved" ? (
                  <AsyncActionButton
                    pendingLabel="Approving..."
                    errorMessage="We could not approve this field. Please try again."
                    onAction={() => onUpdate(`/activity-custom-fields/${row.id}/status`, { status: "Approved" })}
                  >
                    Approve
                  </AsyncActionButton>
                ) : null}
                {row.status !== "Denied" ? (
                  <AsyncActionButton
                    pendingLabel="Denying..."
                    errorMessage="We could not deny this field. Please try again."
                    onAction={() => onUpdate(`/activity-custom-fields/${row.id}/status`, { status: "Denied" })}
                  >
                    Deny
                  </AsyncActionButton>
                ) : null}
              </div>
            )}
          />
        </Panel>
        <Panel title="Dropdown Library" subtitle="Deleted options are hidden from new dropdowns and can be restored later.">
          <ResourceTable
            rows={dropdownOptions}
            columns={[
              ["category", "Dropdown", formatDropdownCategory],
              ["label", "Label"],
              ["value", "Value"],
              ["sortOrder", "Order"],
              ["active", "Status", (value) => (value === false ? "Deleted" : "Visible")],
              ["systemDefault", "Default", (value) => (value ? "Yes" : "No")],
            ]}
            action={(row) => (
              <div className="row-actions">
                <button className="mini-btn" type="button" onClick={() => editDropdownOption(row)}>
                  Edit
                </button>
                {row.active === false ? (
                  <AsyncActionButton
                    pendingLabel="Restoring..."
                    errorMessage="We could not restore this dropdown option. Please try again."
                    onAction={() => setDropdownVisibility(row, true)}
                  >
                    Restore
                  </AsyncActionButton>
                ) : (
                  <AsyncActionButton
                    pendingLabel="Deleting..."
                    errorMessage="We could not delete this dropdown option. Please try again."
                    onAction={() => setDropdownVisibility(row, false)}
                  >
                    Delete
                  </AsyncActionButton>
                )}
              </div>
            )}
          />
        </Panel>
      </section>
      <section className="panel spaced admin-review-panel">
        <div className="panel-header">
          <div>
            <h2>Prospect Deletion Requests</h2>
            <p className="subtle">Approve or deny permanent removal from active prospect views.</p>
          </div>
        </div>
        <ResourceTable
          rows={customerDeletionRequests}
          columns={[
            ["customerName", "Prospect", (value, row) => formatProspectReference(row.customerId, value)],
            ["requestedBy", "Requested By", formatAdminEmployeeReference],
            ["ownerId", "Owner", formatAdminEmployeeReference],
            ["teamId", "Team", (value) => teamName(value)],
            ["status", "Status"],
            ["reason", "Reason"],
            ["reviewedBy", "Reviewed By", (value) => (value ? formatAdminEmployeeReference(value) : "-")],
          ]}
          action={(row) =>
            row.status === "Pending" ? (
              <div className="row-actions review-actions">
                <AsyncActionButton
                  pendingLabel="Approving..."
                  errorMessage="We could not approve this removal request. Please try again."
                  onAction={() => confirmDeletionReview(row, "Approved")}
                >
                  Approve Permanent Removal
                </AsyncActionButton>
                <AsyncActionButton
                  pendingLabel="Denying..."
                  errorMessage="We could not deny this removal request. Please try again."
                  onAction={() => confirmDeletionReview(row, "Denied")}
                >
                  Deny Removal
                </AsyncActionButton>
              </div>
            ) : (
              "-"
            )
          }
        />
      </section>
      <section className="panel spaced">
        <h2>Audit Trail</h2>
        <ResourceTable rows={resources.auditLogs} columns={[["createdAt", "Time"], ["action", "Action"], ["entityType", "Entity"], ["entityId", "ID"], ["userId", "User", formatAdminEmployeeReference]]} />
      </section>
    </>
  );

  function formatAdminEmployeeReference(userId) {
    if (!userId) return "-";
    if (!adminUserById.has(userId) && !fullUserById.has(userId)) return ownerLabel(users, userId);
    return <EmployeeLink userId={userId} users={adminUsers.length ? adminUsers : users} openEmployee={openEmployee} />;
  }

  function formatProspectReference(customerId, customerName) {
    if (!customerId || !customerById.has(customerId)) return customerName || customerId || "-";
    return <CustomerLink customerId={customerId} customers={customers} fallbackName={customerName} openCustomer={openCustomer} />;
  }
}

function formatActivityFieldScope(value) {
  if (value === "call") return "Calls";
  if (value === "visit") return "Visits";
  return "Calls & visits";
}

function ScopeQuickPick({ label, values = [], selectedValues = [], onToggle }) {
  if (!values.length) return null;
  const selected = new Set(selectedValues.map((value) => value.toLowerCase()));
  return (
    <div className="scope-picker full">
      <span>{label}</span>
      <div className="scope-chip-row">
        {values.map((value) => {
          const active = selected.has(String(value).toLowerCase());
          return (
            <button
              className={`scope-chip ${active ? "active" : ""}`}
              type="button"
              aria-pressed={active}
              key={value}
              onClick={() => onToggle(value)}
            >
              {value}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function formatActivityFieldType(value) {
  if (value === "select") return "Select list";
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : "-";
}

function formatScopeList(value) {
  return value?.length ? value.join(", ") : "All";
}

function formatDropdownCategory(value) {
  const category = dropdownCategories.find((item) => item.key === value);
  return category ? `${category.area} - ${category.label}` : value || "-";
}

function parseCsvScope(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}
